import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

import { AlertIcon, CloseIcon } from './icons';
import { completePromptArgument } from '../../services/prompts';

// The form a prompt's arguments need before it can be rendered.
//
// Hand-rolled because assistant-ui has nothing for it: a slash command is
// `{id, label, description, icon, execute: () => void}`, so execute takes no
// parameters and cannot report back. The popover's only two behaviours are
// firing a callback and inserting a mention chip, neither of which carries
// values. Everything around it is still the library's: the menu, the filtering,
// the keyboard, and the composer insert afterwards.
//
// Sits above the composer rather than in a modal. A dialog over a conversation
// hides what somebody is writing about, and this is a step in composing a
// message, not a detour from it.
export default function PromptArguments({
  project,
  bot,
  pending,
  busy,
  error,
  onSubmit,
  onCancel,
}) {
  const { imcp, prompt } = pending;
  const args = prompt.arguments || [];

  const [values, setValues] = useState({});
  // Cache key -> the values the server offered. The key carries the context
  // the answer depends on, not just the argument name: `name` with
  // department=Sales and `name` with department=Engineering are different
  // answers, so a changed context has to MISS rather than serve the previous
  // department's people.
  const [options, setOptions] = useState({});
  const firstField = useRef(null);
  const listID = useId();

  // A fresh form per prompt. Without this, picking a second command inherits
  // the first one's answers, which is worse than empty because it looks filled
  // in.
  useEffect(() => {
    setValues({});
    setOptions({});
    firstField.current?.focus();
  }, [imcp, prompt.name]);

  // contextOf is everything else that is filled in, which is what an
  // argument's candidates depend on and why none of this can be fetched at
  // sync time.
  const contextOf = useCallback(
    (argument) => {
      const filled = {};
      for (const [key, value] of Object.entries(values)) {
        if (key !== argument && value?.trim()) filled[key] = value.trim();
      }

      return filled;
    },
    [values],
  );

  // suggest fetches once per (argument, context, query) and remembers it, so
  // tabbing back into a field costs nothing: every call is a fresh MCP session
  // and an initialize handshake.
  //
  // query is "" on focus. It is only ever non-empty when the server capped its
  // last answer, because then the browser cannot do the filtering itself.
  const suggest = useCallback(
    async (argument, query) => {
      const filled = contextOf(argument);
      const key = cacheKey(imcp, prompt.name, argument, filled, query);

      if (options[key]) return;

      const got = await completePromptArgument(
        project,
        bot,
        imcp,
        prompt.name,
        argument,
        query,
        filled,
      );

      setOptions((current) => ({
        ...current,
        [key]: { values: got.values || [], hasMore: Boolean(got.hasMore), total: got.total || 0 },
      }));
    },
    [project, bot, imcp, prompt.name, contextOf, options],
  );

  // What to show under a field right now, if it has been looked up.
  const shown = (argument) => options[cacheKey(imcp, prompt.name, argument, contextOf(argument), '')];

  // The spec has servers do fuzzy matching and rank by relevance, and caps a
  // response at 100 items. So a capped list must be re-asked as somebody
  // types: filtering 100 of 400 in the browser gives wrong answers, not merely
  // worse ordering. An uncapped list is the whole set and the datalist filters
  // it for free.
  //
  // Debounced, which the spec asks of clients, and skipped entirely in the
  // common case where nothing was capped.
  useEffect(() => {
    const capped = args.filter((arg) => {
      const got = shown(arg.name);

      return got?.hasMore && values[arg.name]?.trim();
    });

    if (capped.length === 0) return undefined;

    const timer = setTimeout(() => {
      for (const arg of capped) {
        suggest(arg.name, values[arg.name].trim());
      }
    }, completionDebounceMs);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const missing = args.filter((arg) => arg.required && !values[arg.name]?.trim());

  const submit = () => {
    if (busy || missing.length > 0) return;

    // Blank optional arguments are left out rather than sent empty: to an MCP
    // server "" and absent are different, and absent is what "leave it blank"
    // means.
    const filled = {};
    for (const arg of args) {
      const value = values[arg.name]?.trim();
      if (value) filled[arg.name] = value;
    }

    onSubmit(filled);
  };

  // A div, NOT a form. ComposerPrimitive.Root is itself a <form>, so a form in
  // here is a nested one: invalid html, and the submit button ends up
  // submitting natively with no action, which reloads the page. Enter and
  // Escape are wired by hand below instead.
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-900">
            {prompt.title || prompt.name}
          </p>
          <p className="truncate text-xs text-gray-500">
            {imcp}
            {prompt.description ? ` · ${prompt.description}` : ''}
          </p>
        </div>

        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel"
          className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
        >
          <CloseIcon className="h-3.5 w-3.5" />
        </button>
      </div>

      {args.map((arg, index) => (
        <label key={arg.name} className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-700">
            {arg.title || arg.name}
            {arg.required && <span className="ml-1 text-red-600">*</span>}
          </span>

          {/* A native datalist rather than a popup of our own: the browser
              gets the filtering, the keyboard and the accessibility, and a
              server with no completions simply leaves it empty. The field
              stays free text either way, which is what an MCP argument is. */}
          <input
            ref={index === 0 ? firstField : undefined}
            type="text"
            list={`${listID}-${arg.name}`}
            value={values[arg.name] || ''}
            onChange={(e) => setValues((v) => ({ ...v, [arg.name]: e.target.value }))}
            onFocus={() => suggest(arg.name, '')}
            onKeyDown={(e) => {
              // Both keys have to stop here. The composer's textarea is a
              // sibling inside the same form, so an unhandled Enter submits
              // the chat and an unhandled Escape cancels the run.
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                submit();
              }

              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                onCancel();
              }
            }}
            // Enter submits the form, not the chat: the composer is a sibling
            // and its Enter-to-send must not fire from in here.
            placeholder={arg.required ? 'Required' : 'Optional'}
            disabled={busy}
            className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100"
          />

          <datalist id={`${listID}-${arg.name}`}>
            {optionsFor(options, imcp, prompt.name, arg.name, contextOf(arg.name), values[arg.name]).map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>

          {/* Shown, not just offered. A datalist gives no sign that suggestions
              exist and most browsers reveal it only on a second click, so an
              argument whose server accepts exactly Text or Blob looked like a
              free text box and got "image" typed into it. A short list is
              rendered as buttons; a long one stays in the datalist, where
              typing is the only sane way through it anyway. */}
          {suggestionsOf(shown(arg.name)).length > 0 && (
            <span className="flex flex-wrap items-center gap-1">
              {suggestionsOf(shown(arg.name)).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setValues((v) => ({ ...v, [arg.name]: option }))}
                  className={`rounded-full border px-2 py-0.5 text-xs transition ${
                    values[arg.name] === option
                      ? 'border-primary-300 bg-primary-50 text-primary-800'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {option}
                </button>
              ))}
            </span>
          )}

          {arg.description && <span className="text-xs text-gray-500">{arg.description}</span>}

          {/* Said out loud when the server capped its answer, or a list that
              is 12 of 400 reads as though it were all of them. */}
          {shown(arg.name)?.hasMore && (
            <span className="text-xs text-gray-400">
              showing some of {shown(arg.name).total || 'many'} matches, keep typing to narrow
            </span>
          )}
        </label>
      ))}

      {error && (
        <p className="flex items-start gap-2 rounded-lg bg-red-50 px-2 py-1.5 text-xs text-red-700">
          <AlertIcon className="mt-px h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1">{error}</span>
        </p>
      )}

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200"
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={submit}
          disabled={busy || missing.length > 0}
          title={missing.length > 0 ? `Fill in ${missing.map((a) => a.title || a.name).join(', ')}` : undefined}
          className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm text-white hover:bg-primary-700 disabled:bg-gray-200 disabled:text-gray-400"
        >
          {busy ? 'Rendering...' : 'Insert'}
        </button>
      </div>
    </div>
  );
}

// completionDebounceMs is how long to wait before re-asking a capped list.
// The spec asks clients to debounce; this only ever runs for a server that
// returned hasMore.
const completionDebounceMs = 250;

// cacheKey identifies one answer: which prompt it is for, the argument, the
// other fields it depends on, and the query the server filtered by.
//
// The prompt is in there because a request that resolves after somebody
// switched prompts would otherwise land under a key the new prompt reads: two
// prompts with an argument of the same name would serve each other's values,
// and the lookup would skip its own request because the key was already taken.
//
// JSON rather than "k=v&k=v", which was ambiguous: {a: "x&b=y"} and
// {a: "x", b: "y"} both flattened to a=x&b=y. Stringify escapes it, and
// sorting the pairs keeps the key stable whatever order the fields were
// filled in.
function cacheKey(imcp, prompt, argument, filled, query) {
  const context = Object.keys(filled)
    .sort()
    .map((key) => [key, filled[key]]);

  return JSON.stringify([imcp, prompt, argument, context, query]);
}

// optionsFor is the list to offer under a field.
//
// A capped list is looked up by the current query, because that is the answer
// the server filtered; it falls back to the unfiltered one while the debounced
// re-ask is still in flight, so the dropdown never blinks empty.
function optionsFor(options, imcp, prompt, argument, filled, value) {
  const query = value?.trim() || '';
  const base = options[cacheKey(imcp, prompt, argument, filled, '')];

  if (query && base?.hasMore) {
    const narrowed = options[cacheKey(imcp, prompt, argument, filled, query)];
    if (narrowed) return narrowed.values;
  }

  return base?.values || [];
}

// maxSuggestionChips is how many values are worth showing as buttons. Past
// this the row becomes a wall and typing is the better way through, which is
// what the datalist is for.
const maxSuggestionChips = 8;

// suggestionsOf is the values worth rendering as chips: a short, complete list.
// A capped one is not complete, so showing part of it as though it were the
// choices would be worse than showing none.
function suggestionsOf(shown) {
  if (!shown || shown.hasMore) return [];
  if (shown.values.length > maxSuggestionChips) return [];

  return shown.values;
}
